// plugin-market.js - the plugin publishing path UI: upload (registered users),
// docs + template + sample, admin review queue (approve/reject), per-user "my
// plugins" tracking, and per-plugin ratings/reviews. Talks to PluginsController.
// The loader (plugin-loader.js) wires the catalog toolbar to these and calls
// renderRatings() inside each opened plugin panel.
import { h, createHost } from './plugin-host.js';
import { createSandbox } from './plugin-sandbox.js';

// Client-side AUTO-REVIEW dry-run: load the plugin in the SAME hardened sandbox it will run
// in for real, validate the manifest schema, run() it with default params under the watchdog
// + payload caps, and check the result envelope and the trust/references honesty rule. This
// proves the plugin actually runs in its isolation, not just that it parses. Returns {ok, reason}.
function trustVerdictOf(t) {
  if (!t || typeof t !== 'object' || t.untracked) return null;
  const v = t.geonum_trust || t.trust || t.trust_verdict || t.trust_tier;
  if (typeof v === 'string' && /^(EXACT|PRECISE|VALID)$/i.test(v.trim())) return v.trim().toUpperCase();
  return null; // DEGRADED/UNRELIABLE/CANCELLED/[UNTRACKED] are honest, no reference claim
}
async function dryRun(source, tier) {
  const host = createHost({ id: 'dryrun', tier: tier }, null); // mediated engine surface only
  const sandbox = createSandbox(source, { engine: (n, r) => host.engine(n, r), allow: host.engines, onLog: () => {}, timeoutMs: 10000 });
  try {
    let schema;
    try { schema = await sandbox.init(); } catch (e) { return { ok: false, reason: 'failed to load in the sandbox: ' + ((e && e.message) || e) }; }
    if (!schema || typeof schema.id !== 'string' || !schema.id) return { ok: false, reason: 'manifest is missing a valid id' };
    if (!schema.name) return { ok: false, reason: 'manifest is missing name' };
    if (!schema.tier) return { ok: false, reason: 'manifest is missing tier' };
    if (schema.tier !== tier) return { ok: false, reason: 'manifest tier "' + schema.tier + '" does not match the selected module "' + tier + '"' };
    if (schema.inputs != null && !Array.isArray(schema.inputs)) return { ok: false, reason: 'inputs must be an array' };
    const params = {}; (schema.inputs || []).forEach(i => { if (i && i.key != null) params[i.key] = i.default; });
    let res;
    try { res = await sandbox.run(params); } catch (e) { return { ok: false, reason: 'run() failed in the sandbox: ' + ((e && e.message) || e) }; }
    if (!res || typeof res !== 'object' || !('status' in res || 'summary' in res || 'charts' in res || 'viewport3d' in res || 'html' in res))
      return { ok: false, reason: 'run() must return an object with at least one of: status, summary, charts, viewport3d, html' };
    const verdict = trustVerdictOf(res.trust);
    const hasRefs = Array.isArray(schema.references) && schema.references.length > 0;
    if (verdict && !hasRefs) return { ok: false, reason: 'a trust verdict (' + verdict + ') requires published references in the manifest - the platform never shows a pass without a citation' };
    return { ok: true, reason: 'passed the sandboxed dry-run' };
  } finally { try { sandbox.dispose(); } catch (e) {} }
}

export function token() { try { return localStorage.getItem('gdbs_token') || ''; } catch (e) { return ''; } }
export function isRegistered() { return !!token(); }
export function authFetch(url, opts) {
  opts = opts || {};
  const t = token();
  opts.headers = Object.assign({ 'Content-Type': 'application/json' }, opts.headers || {}, t ? { Authorization: 'Bearer ' + t } : {});
  return fetch(url, opts);
}
function download(filename, text, type) {
  const blob = new Blob([text], { type: type || 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 100);
}

// ---- generic modal ----
function modal(title, accent, contentEl) {
  let ov = document.getElementById('plugin-market-overlay');
  if (ov) ov.remove();
  ov = h('div', { id: 'plugin-market-overlay', style: { position: 'fixed', inset: '0', zIndex: '1300', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '36px 16px', overflow: 'auto' } });
  ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  const card = h('div', { style: { maxWidth: '820px', width: '100%', margin: 'auto', border: '1px solid ' + accent, borderRadius: '8px', background: 'var(--bg-card,var(--bg-panel,#1e293b))', boxShadow: '0 16px 48px rgba(0,0,0,0.5)', fontSize: '13px', color: 'var(--text,#1f2937)', maxHeight: '88vh', overflow: 'auto' } },
    h('div', { style: { position: 'sticky', top: '0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: accent + '24', borderBottom: '1px solid ' + accent } },
      h('b', { style: { color: accent } }, title),
      h('button', { type: 'button', style: { background: 'none', border: 'none', color: 'var(--text-dim,#64748b)', fontSize: '18px', cursor: 'pointer' }, onclick: () => ov.remove() }, '×')),
    h('div', { style: { padding: '14px' } }, contentEl));
  ov.appendChild(card);
  document.body.appendChild(ov);
  return ov;
}
function notice(el, msg, ok) { const c = ok === true ? '#16a34a' : ok === false ? '#ef4444' : '#60a5fa'; el.innerHTML = ''; el.appendChild(h('div', { style: { margin: '8px 0', padding: '8px 11px', borderRadius: '6px', fontSize: '12px', border: '1px solid ' + c, background: c + '14', color: c } }, msg)); }

// ---- templates / sample (embedded, no server dependency) ----
const TEMPLATE_ES = `// my_theory.plugin.es - a GDBS plugin (ES module). Drop into /plugins/{std,pro,hpc}/.
// Plugins are self-contained: no imports. The host 'gdbs' is passed to run().
export default {
  id: 'my_theory',
  tier: 'hpc',                       // 'std' | 'pro' | 'hpc' (must match its folder)
  name: 'My Theory',
  icon: 'fa-atom',                   // FontAwesome 5 (without 'fas')
  accent: '#a78bfa',
  blurb: 'One line shown under the title.',
  version: '1.0.0',
  references: ['Author A, Author B (2020). Title. Journal vol, page. DOI.'],  // PUBLISHED only
  inputs: [
    { key: 'n', label: 'Grid N', type: 'number', default: 64, min: 8, max: 256, step: 8 },
    { key: 'mode', label: 'Mode', type: 'select', default: 'a', options: [{ v: 'a', l: 'Alpha' }, { v: 'b', l: 'Beta' }] }
  ],
  presets: { 'Fast': { n: 32 } },
  // Optional self-documentation (shown in the plugin's dev-log / bug-log):
  devlog: [{ stage: 'Closed form', detail: '...', validated: 'vs Published X' }],
  buglog: [{ title: 'No defects surfaced in validation', status: 'clean' }],
  async run(params, gdbs) {
    // call any engine:  const r = await gdbs.engine('multigrid', { nx: params.n, ... });
    return {
      summary: [['Result', 42]],
      // trust binds automatically; return a result with a GeoNum verdict to show it
      charts: [{ type: 'line', x: [0,1,2], series: [{ name: 'y', y: [0,1,4] }], xlabel: 't', ylabel: 'y' }],
      // viewport3d: { points: [[x,y,z],...], values: [...], title: '...' }
    };
  }
};
`;
const TEMPLATE_GDB = `{
  "id": "my_theory",
  "tier": "hpc",
  "name": "My Theory",
  "icon": "fa-layer-group",
  "accent": "#34d399",
  "blurb": "Declarative plugin: no JavaScript - maps a form to an engine.",
  "version": "1.0.0",
  "references": ["Author (Year). Title. Journal."],
  "inputs": [{ "key": "grid_n", "label": "Grid N", "type": "number", "default": 32, "min": 8, "max": 64, "step": 8 }],
  "engine": "multigrid",
  "map": { "nx": "grid_n", "ny": "grid_n", "nz": "grid_n", "box_length": 6.283185307179586, "n_cycles": 8, "source": "sine_mode", "k_mode": 1, "omega": 1.0 },
  "outputs": {
    "summary": [["Final residual", "final_residual"], ["L2 vs exact", "l2_vs_exact"]],
    "trust": true,
    "charts": [{ "type": "line", "x": null, "series": [{ "name": "residual", "y": "residual_history" }], "xlabel": "V-cycle", "ylabel": "||r||" }]
  }
}
`;

export function openDocsModal() {
  const body = h('div', {});
  body.appendChild(h('p', { style: { color: '#94a3b8', fontSize: '12px' } },
    'A plugin is a single self-contained file dropped into /plugins/{std,pro,hpc}/. Two forms: a .plugin.es ES module with a run() function (full power - call any engine, build charts and 3D), or a declarative .gdb (no code: map a form to an engine). GeoNum trust binds automatically. References must be PUBLISHED.'));
  body.appendChild(h('ol', { style: { fontSize: '12px', color: 'var(--text,#334155)', margin: '6px 0 10px 18px', lineHeight: '1.6' } },
    h('li', {}, 'Start from a template below; set id, tier, name, icon, references, inputs.'),
    h('li', {}, 'Implement run(params, gdbs) (or use the .gdb engine+map). Return {summary, charts, viewport3d, html}.'),
    h('li', {}, 'Upload it here (Upload a plugin). It is stored UNVERIFIED until an admin reviews and approves it.'),
    h('li', {}, 'Once approved it appears in the catalog for everyone, badged "User".')));
  const tmpl = h('textarea', { class: 'form-control', readonly: 'readonly', style: { width: '100%', height: '220px', fontFamily: 'monospace', fontSize: '11px' } });
  tmpl.value = TEMPLATE_ES;
  const fmtRow = h('div', { style: { display: 'flex', gap: '8px', margin: '6px 0', flexWrap: 'wrap' } },
    h('button', { class: 'btn btn-sm', onclick: () => { tmpl.value = TEMPLATE_ES; } }, '.plugin.es template'),
    h('button', { class: 'btn btn-sm', onclick: () => { tmpl.value = TEMPLATE_GDB; } }, '.gdb template'),
    h('button', { class: 'btn btn-sm', onclick: () => { navigator.clipboard && navigator.clipboard.writeText(tmpl.value); } }, 'Copy'),
    h('button', { class: 'btn btn-sm', onclick: () => download('my_theory.plugin.es', TEMPLATE_ES, 'text/javascript') }, 'Download .es template'),
    h('button', { class: 'btn btn-sm', onclick: () => download('my_theory.gdb', TEMPLATE_GDB, 'application/json') }, 'Download .gdb template'),
    h('button', { class: 'btn btn-sm btn-primary', onclick: () => { modalClose(); openUploadModal(); } }, 'Upload a plugin'));
  body.appendChild(fmtRow);
  body.appendChild(tmpl);
  modal('How to create a plugin', '#3b82f6', body);
}
function modalClose() { const ov = document.getElementById('plugin-market-overlay'); if (ov) ov.remove(); }

export function openUploadModal(editId) {
  const body = h('div', {});
  if (!isRegistered()) {
    body.appendChild(h('div', { style: { color: '#fbbf24', fontSize: '13px' } }, 'You must be signed in to upload a plugin. Please register or sign in first.'));
    modal('Upload a plugin', '#a78bfa', body); return;
  }
  const sel = (id, label, opts) => h('label', { style: { fontSize: '12px', color: '#888', display: 'flex', flexDirection: 'column', gap: '3px' } }, label,
    h('select', { id, class: 'form-control', style: { width: 'auto' } }, ...opts.map(o => h('option', { value: o.v }, o.l))));
  const inp = (id, label, ph) => h('label', { style: { fontSize: '12px', color: '#888', display: 'flex', flexDirection: 'column', gap: '3px', flex: '1' } }, label,
    h('input', { id, class: 'form-control', placeholder: ph || '' }));
  const tierSel = sel('up-tier', 'Module', [{ v: 'std', l: 'Standard' }, { v: 'pro', l: 'Pro' }, { v: 'hpc', l: 'HPC Lab' }]);
  const fmtSel = sel('up-fmt', 'Format', [{ v: 'es', l: '.plugin.es (code)' }, { v: 'gdb', l: '.gdb (declarative)' }]);
  const nameI = inp('up-name', 'Name', 'My Theory');
  const blurbI = inp('up-blurb', 'Blurb (one line)', 'short description');
  const iconI = inp('up-icon', 'Icon (fa-...)', 'fa-atom');
  const tagsI = inp('up-tags', 'Tags (comma-separated)', 'e.g. cfd, validation, gpu');
  const ta = h('textarea', { id: 'up-src', class: 'form-control', placeholder: 'Paste your .plugin.es or .gdb source here, or choose a file below.', style: { width: '100%', height: '200px', fontFamily: 'monospace', fontSize: '11px', marginTop: '8px' } });
  const file = h('input', { type: 'file', accept: '.es,.js,.gdb,.json,.txt', style: { marginTop: '6px' }, onchange: (e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => { ta.value = r.result; if (/\.gdb/i.test(f.name)) document.getElementById('up-fmt').value = 'gdb'; }; r.readAsText(f); } });
  const msg = h('div', {});
  const submit = h('button', { class: 'btn btn-primary', style: { marginTop: '10px' }, onclick: async () => {
    submit.disabled = true;
    try {
      const fmt = document.getElementById('up-fmt').value;
      const tier = document.getElementById('up-tier').value;
      const source = ta.value;
      if (!editId && !source.trim()) { notice(msg, 'Source is empty.', false); submit.disabled = false; return; }
      // CLIENT-SIDE AUTO-REVIEW: run the plugin once in the SAME sandbox it will run in for
      // real (schema + dry-run under the watchdog + payload caps), so it can't pass static
      // checks then fail at runtime. .gdb is declarative (no code) -> no dry-run.
      if (fmt === 'es' && source.trim()) {
        notice(msg, 'Auto-review: validating in the sandbox (dry-run)...', null);
        const dr = await dryRun(source, tier);
        if (!dr.ok) { notice(msg, 'Auto-rejected by the sandbox dry-run: ' + dr.reason, false); submit.disabled = false; return; }
      }
      const payload = { tier: tier, format: fmt, name: document.getElementById('up-name').value, blurb: document.getElementById('up-blurb').value, icon: document.getElementById('up-icon').value, source: source,
        tags: (document.getElementById('up-tags').value || '').split(',').map(s => s.trim()).filter(Boolean) };
      const url = editId ? ('/api/plugins/' + encodeURIComponent(editId) + '/update') : '/api/plugins/submit';
      const r = await authFetch(url, { method: 'POST', body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        // Server is the security authority: it re-runs the static screen and decides.
        const ok = (j.status === 'approved');
        notice(msg, editId ? ('Updated' + (j.status === 'rejected' ? ' but auto-rejected: re-check the source.' : ' and live.')) : (j.message || (ok ? 'Auto-approved and live.' : 'Submitted.')), ok);
        try { const mine = document.getElementById('plugin-mine'); if (mine) renderMyPlugins(mine); } catch (e) {}
      } else { notice(msg, j.error || ((editId ? 'Update' : 'Submit') + ' failed (' + r.status + ')'), false); }
    } catch (e) { notice(msg, 'Error: ' + e.message, false); }
    submit.disabled = false;
  } }, h('i', { class: 'fas fa-' + (editId ? 'save' : 'upload') }), editId ? ' Save changes' : ' Submit for review');
  body.appendChild(h('p', { style: { color: '#94a3b8', fontSize: '12px' } }, editId
    ? 'Editing your plugin. Metadata applies immediately; a SOURCE change is re-screened by the auto-reviewer (security + a sandboxed dry-run).'
    : 'Uploads are automatically screened on submission (security screen + a sandboxed dry-run in the same isolation users get). A clean plugin goes live immediately at unverified trust and earns its rating from the community; a violation is auto-rejected with the reason. The worker sandbox is the runtime guarantee.'));
  body.appendChild(h('div', { style: { display: 'flex', gap: '10px', flexWrap: 'wrap' } }, tierSel, fmtSel, nameI));
  body.appendChild(h('div', { style: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '6px' } }, blurbI, iconI, tagsI));
  body.appendChild(file); body.appendChild(ta); body.appendChild(submit); body.appendChild(msg);
  modal(editId ? 'Edit plugin' : 'Upload a plugin', '#a78bfa', body);
  // edit mode: fetch the existing submission and pre-fill the form
  if (editId) {
    authFetch('/api/plugins/' + encodeURIComponent(editId) + '/edit').then(r => r.ok ? r.json() : null).then(d => {
      if (!d) { notice(msg, 'Could not load this plugin for editing.', false); return; }
      document.getElementById('up-tier').value = d.tier || 'std';
      document.getElementById('up-fmt').value = d.format || 'es';
      document.getElementById('up-name').value = d.name || '';
      document.getElementById('up-blurb').value = d.blurb || '';
      document.getElementById('up-icon').value = d.icon || '';
      document.getElementById('up-tags').value = (d.tags || []).join(', ');
      ta.value = d.source || '';
    }).catch(() => {});
  }
}

// Admin editor for a BUILT-IN (first-party) plugin static file.
export function openBuiltinEditor(tier, file, name, onSaved) {
  const body = h('div', {});
  const ta = h('textarea', { class: 'form-control', style: { width: '100%', height: '340px', fontFamily: 'monospace', fontSize: '11px' }, placeholder: 'loading source...' });
  const msg = h('div', {});
  const save = h('button', { class: 'btn btn-primary', style: { marginTop: '10px' }, onclick: async () => {
    save.disabled = true;
    let r; try { r = await authFetch('/api/plugins/builtin/save', { method: 'POST', body: JSON.stringify({ tier: tier, file: file, source: ta.value }) }); } catch (e) { r = null; }
    const j = r ? await r.json().catch(() => ({})) : {};
    if (r && r.ok) { notice(msg, j.note || 'Saved.', true); if (onSaved) try { onSaved(); } catch (e) {} }
    else { notice(msg, j.error || ('Save failed' + (r ? ' (' + r.status + ')' : '')), false); }
    save.disabled = false;
  } }, h('i', { class: 'fas fa-save' }), ' Save (live file)');
  body.appendChild(h('p', { style: { color: '#fbbf24', fontSize: '12px' } }, 'Editing the BUILT-IN ' + (name || file) + ' [' + tier + ']. This writes the LIVE file directly; update the repo copy too, or the next deploy overwrites it.'));
  body.appendChild(ta); body.appendChild(save); body.appendChild(msg);
  modal('Edit built-in: ' + (name || file), '#a78bfa', body);
  authFetch('/api/plugins/builtin/source?tier=' + encodeURIComponent(tier) + '&file=' + encodeURIComponent(file))
    .then(r => r.ok ? r.json() : null).then(d => { if (d) ta.value = d.source || ''; else notice(msg, 'Could not load source (admin only).', false); }).catch(() => {});
}

// Admin VSS review queue: grant the "VSS Approved" QUALITY blessing (the human curation layer
// on top of the automated safety screen). Approved plugins sort to the top of the catalog.
export async function openVssQueue(onApprove) {
  const body = h('div', {});
  body.appendChild(h('p', { style: { fontSize: '12px', color: '#94a3b8', margin: '0 0 8px' } },
    'Grant the "VSS Approved" quality blessing. These plugins already passed the automated safety screen and are live at unverified trust; approving one endorses its QUALITY and sorts it to the top.'));
  const status = h('div', { style: { fontSize: '12px', color: '#94a3b8', marginBottom: '8px' } }, 'Loading...');
  const list = h('div', {});
  body.appendChild(status); body.appendChild(list);
  modal('VSS review queue', '#fbbf24', body);
  let r; try { r = await authFetch('/api/plugins/vss-queue'); } catch (e) { r = null; }
  if (!r || !r.ok) { status.textContent = 'Could not load (admin only).'; return; }
  const items = (await r.json().catch(() => ({ plugins: [] }))).plugins || [];
  status.textContent = items.length ? (items.length + ' awaiting VSS review') : 'Nothing awaiting VSS review.';
  items.forEach(s => {
    const src = h('pre', { style: { display: 'none', maxHeight: '220px', overflow: 'auto', fontSize: '10.5px', background: 'rgba(148,163,184,0.08)', padding: '8px', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: '4px' } }, 'loading...');
    const row = h('div', { style: { borderBottom: '1px solid rgba(148,163,184,0.15)', padding: '6px 0' } });
    const approve = h('button', { class: 'btn btn-sm', style: { borderColor: '#fbbf24', color: '#fbbf24' }, onclick: async () => {
      approve.disabled = true;
      let rr; try { rr = await authFetch('/api/plugins/' + encodeURIComponent(s.id) + '/vss-approve', { method: 'POST' }); } catch (e) { rr = null; }
      if (rr && rr.ok) { row.remove(); if (onApprove) try { onApprove(s.id); } catch (e) {} } else { approve.disabled = false; alert('Approve failed' + (rr ? ' (' + rr.status + ')' : '')); }
    } }, h('i', { class: 'fas fa-circle-check' }), ' VSS Approve');
    const viewBtn = h('button', { class: 'btn btn-sm', onclick: async () => {
      const showing = src.style.display !== 'none';
      src.style.display = showing ? 'none' : 'block';
      if (!showing && src.textContent === 'loading...') { try { const rs = await authFetch('/api/plugins/source/' + encodeURIComponent(s.id)); src.textContent = rs.ok ? await rs.text() : 'could not load'; } catch (e) { src.textContent = 'error'; } }
    } }, 'View source');
    row.appendChild(h('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' } },
      h('b', {}, s.name || s.id), h('span', { style: { fontSize: '10px', color: '#94a3b8' } }, '[' + s.tier + '/' + s.format + '] by ' + (s.submitterName || '?')),
      viewBtn, approve));
    row.appendChild(src);
    list.appendChild(row);
  });
}

export async function openReviewQueue() {
  const body = h('div', {});
  body.appendChild(h('div', { style: { color: '#94a3b8', fontSize: '12px' } }, 'Loading pending submissions...'));
  modal('Plugin review queue (admin)', '#ef4444', body);
  let r;
  try { r = await authFetch('/api/plugins/pending'); } catch (e) { body.innerHTML = 'network error'; return; }
  if (r.status === 403 || r.status === 401) { body.innerHTML = '<div style="color:#fbbf24">Admin only.</div>'; return; }
  const j = await r.json().catch(() => ({ plugins: [] }));
  const list = j.plugins || [];
  body.innerHTML = '';
  if (!list.length) { body.appendChild(h('div', { style: { color: '#64748b' } }, 'No pending submissions.')); return; }
  list.forEach(s => {
    const src = h('textarea', { class: 'form-control', readonly: 'readonly', style: { width: '100%', height: '160px', fontFamily: 'monospace', fontSize: '11px', display: 'none' } });
    src.value = s.source || '';
    const status = h('span', { style: { marginLeft: '8px', fontSize: '12px' } });
    const row = h('div', { style: { border: '1px solid var(--border,#334155)', borderRadius: '6px', padding: '10px', marginBottom: '10px' } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
        h('b', {}, s.name || s.id), h('span', { style: { fontSize: '10px', color: '#94a3b8' } }, '[' + s.tier + ' / ' + s.format + ']  by ' + (s.submitterName || '?')),
        h('button', { class: 'btn btn-sm', onclick: () => { src.style.display = src.style.display === 'none' ? 'block' : 'none'; } }, 'View source'),
        h('button', { class: 'btn btn-sm', style: { borderColor: '#16a34a', color: '#16a34a' }, onclick: async () => { const rr = await authFetch('/api/plugins/' + s.id + '/approve', { method: 'POST' }); status.textContent = rr.ok ? 'approved' : 'failed'; status.style.color = rr.ok ? '#16a34a' : '#ef4444'; } }, 'Approve'),
        h('button', { class: 'btn btn-sm', style: { borderColor: '#ef4444', color: '#ef4444' }, onclick: async () => { const reason = prompt('Reject reason (optional):') || ''; const rr = await authFetch('/api/plugins/' + s.id + '/reject', { method: 'POST', body: JSON.stringify({ reason }) }); status.textContent = rr.ok ? 'rejected' : 'failed'; status.style.color = rr.ok ? '#d97706' : '#ef4444'; } }, 'Reject'),
        status),
      s.blurb ? h('div', { style: { fontSize: '11px', color: '#94a3b8', margin: '4px 0' } }, s.blurb) : null,
      s.format === 'es' ? h('div', { style: { fontSize: '10px', color: '#f59e0b' } }, 'Contains executable code - review carefully before approving.') : null,
      src);
    body.appendChild(row);
  });
}

export async function renderMyPlugins(container) {
  container.innerHTML = '';
  if (!isRegistered()) return;
  let r; try { r = await authFetch('/api/plugins/mine'); } catch (e) { return; }
  if (!r.ok) return;
  const j = await r.json().catch(() => ({ plugins: [] }));
  const list = j.plugins || [];
  if (!list.length) return;
  container.appendChild(h('h3', { style: { fontSize: '14px', margin: '8px 0 6px' } }, 'My submissions'));
  const col = { pending: '#d97706', approved: '#16a34a', rejected: '#ef4444' };
  list.forEach(s => {
    const row = h('div', { style: { fontSize: '12px', margin: '3px 0', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' } });
    row.appendChild(h('span', { style: { fontWeight: '600' } }, s.name));
    row.appendChild(h('span', { style: { fontSize: '10px', color: '#94a3b8' } }, '[' + s.tier + '/' + s.format + ']'));
    row.appendChild(h('span', { style: { color: col[s.status] || '#94a3b8', fontWeight: '600' } }, s.status));
    if (s.rejectReason) row.appendChild(h('span', { style: { color: '#94a3b8', fontStyle: 'italic' } }, '- ' + s.rejectReason));
    const edit = h('button', { class: 'btn btn-sm', style: { padding: '1px 8px', fontSize: '11px' }, onclick: () => { modalClose(); openUploadModal(s.id); } }, h('i', { class: 'fas fa-pen' }), ' Edit');
    const del = h('button', { class: 'btn btn-sm', style: { padding: '1px 8px', fontSize: '11px', borderColor: '#ef4444', color: '#ef4444' }, onclick: async () => {
      if (!confirm('Delete "' + s.name + '"? This removes the plugin and its reviews.')) return;
      del.disabled = true;
      let rr; try { rr = await authFetch('/api/plugins/' + encodeURIComponent(s.id) + '/delete', { method: 'POST' }); } catch (e) { rr = null; }
      if (rr && rr.ok) row.remove(); else { del.disabled = false; alert('Delete failed' + (rr ? ' (' + rr.status + ')' : '')); }
    } }, h('i', { class: 'fas fa-trash' }), ' Delete');
    row.appendChild(h('span', { style: { marginLeft: 'auto', display: 'inline-flex', gap: '6px' } }, edit, del));
    container.appendChild(row);
  });
}

// ---- ratings / reviews (shown inside an opened plugin panel) ----
function stars(n, max) { let s = ''; for (let i = 1; i <= (max || 5); i++) s += i <= Math.round(n) ? '★' : '☆'; return s; }
// Fallback aspect tags if the server's allTags is unavailable; the server is authoritative.
const FALLBACK_TAGS = ['Accurate', 'Easy to use', 'Well documented', 'Fast', 'Trustworthy', 'Reproducible', 'Good validation', 'Needs work'];

export async function renderRatings(container, pluginId) {
  container.innerHTML = '';
  container.appendChild(h('h4', { style: { fontSize: '13px', margin: '10px 0 4px' } }, 'Ratings & reviews'));
  const summaryEl = h('div', {});
  const tagsEl = h('div', { style: { margin: '6px 0' } });
  const listEl = h('div', {});
  container.appendChild(summaryEl); container.appendChild(tagsEl); container.appendChild(listEl);

  let data = { avg: 0, count: 0, dist: {}, tags: [], allTags: FALLBACK_TAGS, reviews: [] };
  let filterTag = null;

  function chip(label, active, onClick) {
    return h('button', { type: 'button', style: { fontSize: '11px', padding: '2px 9px', borderRadius: '9999px', cursor: 'pointer', whiteSpace: 'nowrap',
      border: '1px solid ' + (active ? '#a78bfa' : 'var(--border,#334155)'), background: active ? 'rgba(167,139,250,0.18)' : 'transparent', color: active ? '#a78bfa' : 'var(--text-dim,#94a3b8)' }, onclick: onClick }, label);
  }

  function renderSummary() {
    summaryEl.innerHTML = '';
    summaryEl.appendChild(h('div', { style: { fontSize: '14px', color: '#f59e0b', fontWeight: '600' } }, stars(data.avg) + ' ' + (data.avg || 0) + ' / 5  (' + (data.count || 0) + ')'));
    const total = data.count || 0;
    if (total) {
      const hist = h('div', { style: { margin: '4px 0 2px' } });
      for (let s = 5; s >= 1; s--) {
        const c = (data.dist && data.dist[s]) || 0;
        const pct = total ? Math.round(c / total * 100) : 0;
        const bar = h('span', { style: { flex: '1', height: '8px', maxWidth: '180px', background: 'rgba(148,163,184,0.2)', borderRadius: '4px', overflow: 'hidden' } },
          h('span', { style: { display: 'block', height: '100%', width: pct + '%', background: '#f59e0b' } }));
        hist.appendChild(h('div', { style: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#94a3b8', margin: '1px 0' } },
          h('span', { style: { width: '30px' } }, s + '★'), bar, h('span', { style: { width: '26px', textAlign: 'right' } }, String(c))));
      }
      summaryEl.appendChild(hist);
    }
  }

  function renderTagBar() {
    tagsEl.innerHTML = '';
    if (!(data.tags && data.tags.length)) return;
    tagsEl.appendChild(h('div', { style: { fontSize: '12px', color: '#888', marginBottom: '3px' } }, 'Reviewers mention:'));
    const row = h('div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap' } });
    data.tags.forEach(t => row.appendChild(chip(t.tag + ' (' + t.count + ')', filterTag === t.tag, () => { filterTag = (filterTag === t.tag) ? null : t.tag; renderTagBar(); renderList(); })));
    if (filterTag) row.appendChild(chip('× clear', false, () => { filterTag = null; renderTagBar(); renderList(); }));
    tagsEl.appendChild(row);
  }

  function renderList() {
    listEl.innerHTML = '';
    let revs = data.reviews || [];
    if (filterTag) revs = revs.filter(r => (r.tags || []).indexOf(filterTag) !== -1);
    if (!revs.length) { listEl.appendChild(h('div', { style: { fontSize: '11px', color: '#64748b', margin: '4px 0' } }, filterTag ? 'No reviews with that tag.' : 'No reviews yet.')); return; }
    revs.slice(0, 20).forEach(rv => {
      const item = h('div', { style: { fontSize: '11px', color: '#94a3b8', margin: '6px 0', paddingBottom: '6px', borderBottom: '1px solid rgba(148,163,184,0.12)' } });
      item.appendChild(h('div', {}, h('span', { style: { color: '#f59e0b' } }, stars(rv.stars)), ' ', h('b', { style: { color: 'var(--text,#e2e8f0)' } }, rv.userName || 'user')));
      if (rv.review) item.appendChild(h('div', { style: { margin: '2px 0' } }, rv.review));
      if (rv.tags && rv.tags.length) {
        const tr = h('div', { style: { display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' } });
        rv.tags.forEach(t => tr.appendChild(h('span', { style: { fontSize: '10px', padding: '1px 7px', borderRadius: '9999px', background: 'rgba(167,139,250,0.12)', color: '#a78bfa' } }, t)));
        item.appendChild(tr);
      }
      listEl.appendChild(item);
    });
  }

  async function refresh() {
    let r; try { r = await fetch('/api/plugins/ratings?id=' + encodeURIComponent(pluginId)); } catch (e) { return; }
    data = await r.json().catch(() => data);
    if (!data.allTags || !data.allTags.length) data.allTags = FALLBACK_TAGS;
    renderSummary(); renderTagBar(); renderList();
  }
  await refresh();

  if (isRegistered()) {
    let chosen = 5;
    const selTags = new Set();
    const starBtns = h('span', {});
    for (let i = 1; i <= 5; i++) { (function (k) { const b = h('span', { style: { cursor: 'pointer', color: '#f59e0b', fontSize: '18px' }, onclick: () => { chosen = k; for (let j = 0; j < 5; j++) starBtns.children[j].textContent = j < k ? '★' : '☆'; } }, k <= chosen ? '★' : '☆'); starBtns.appendChild(b); })(i); }
    const tagPick = h('div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '4px 0' } });
    function paint(b, on) { b.style.background = on ? 'rgba(167,139,250,0.18)' : 'transparent'; b.style.color = on ? '#a78bfa' : 'var(--text-dim,#94a3b8)'; b.style.borderColor = on ? '#a78bfa' : 'var(--border,#334155)'; }
    (data.allTags || FALLBACK_TAGS).forEach(t => { const b = chip(t, false, () => { if (selTags.has(t)) { selTags.delete(t); paint(b, false); } else { selTags.add(t); paint(b, true); } }); tagPick.appendChild(b); });
    const rev = h('input', { class: 'form-control', placeholder: 'optional review', style: { width: '240px' } });
    const rateMsg = h('span', { style: { fontSize: '11px', marginLeft: '4px' } });
    const submit = h('button', { class: 'btn btn-sm', onclick: async () => {
      if (!(chosen >= 1 && chosen <= 5)) { rateMsg.textContent = 'pick 1-5 stars'; rateMsg.style.color = '#ef4444'; return; }
      submit.disabled = true; rateMsg.textContent = 'saving...'; rateMsg.style.color = '#94a3b8';
      // id + tags in the BODY (not the path) so built-in ids with '/' are not %2F-404'd by IIS.
      let r; try { r = await authFetch('/api/plugins/rate', { method: 'POST', body: JSON.stringify({ pluginId: pluginId, stars: chosen, review: rev.value, tags: [...selTags] }) }); } catch (e) { r = null; }
      submit.disabled = false;
      if (r && r.ok) { rev.value = ''; selTags.clear(); Array.from(tagPick.children).forEach(b => paint(b, false)); rateMsg.textContent = 'saved'; rateMsg.style.color = '#16a34a'; refresh(); }
      else { rateMsg.textContent = (r && r.status === 401) ? 'sign in to rate' : 'could not save rating'; rateMsg.style.color = '#ef4444'; }
    } }, 'Submit rating');
    const form = h('div', { style: { marginTop: '8px', borderTop: '1px solid rgba(148,163,184,0.15)', paddingTop: '8px' } });
    form.appendChild(h('div', { style: { fontSize: '12px', color: '#888', marginBottom: '2px' } }, 'Your rating:'));
    form.appendChild(starBtns);
    form.appendChild(h('div', { style: { fontSize: '11px', color: '#888', margin: '6px 0 2px' } }, 'Tags (optional):'));
    form.appendChild(tagPick);
    form.appendChild(h('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '4px' } }, rev, submit, rateMsg));
    container.appendChild(form);
  } else {
    container.appendChild(h('div', { style: { fontSize: '11px', color: '#64748b', marginTop: '6px' } }, 'Sign in to rate this plugin.'));
  }
}

export function originBadge(origin) {
  const gdbs = origin === 'gdbs';
  const c = gdbs ? '#a78bfa' : '#22c55e';
  return h('span', { title: gdbs ? 'Official GDBS plugin' : 'Community-uploaded, admin-approved', style: { fontSize: '9px', fontWeight: '700', color: c, border: '1px solid ' + c + '66', borderRadius: '9999px', padding: '0 6px' } }, gdbs ? 'GDBS' : 'User');
}
